import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useStore } from "@/context/StoreContext";
import { formatPrice } from "@/lib/api";
import { FALLBACK_IMG } from "@/lib/images";
import { EASE } from "@/components/motion";
import { Heart, Plus } from "lucide-react";

export function ProductCard({ product, index = 0, arch = false }) {
  const { addToCart, toggleFavorite, favorites } = useStore();
  const fav = favorites.includes(product.slug);
  const outOfStock = product.stockStatus === "out_of_stock" || product.stock === 0;
  const hasChoices = (product.variants?.length || 0) > 1;

  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: EASE }}
      className="group relative"
    >
      <Link to={`/urun/${product.slug}`} className="block">
        <div className={`relative overflow-hidden border border-shule-line bg-shule-cream ${arch ? "rounded-t-[100px] rounded-b-3xl" : "rounded-3xl"}`}>
          <div className="aspect-[3/4]">
            <img
              src={product.images?.[0] || FALLBACK_IMG}
              alt={product.name}
              loading="lazy"
              onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
            />
          </div>
          {product.isNew && (
            <span className="absolute top-3 left-3 badge-tag !bg-shule-goldlight/90">Yeni</span>
          )}
          {!product.isNew && product.isBestseller && (
            <span className="absolute top-3 left-3 badge-tag !bg-shule-goldlight/90">Çok Satan</span>
          )}
          {!product.isNew && !product.isBestseller && product.compare_at && (
            <span className="absolute top-3 left-3 badge-tag !bg-shule-goldlight/90">Ayrıcalıklı</span>
          )}
          {product.is_demo && (
            <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-wider text-shule-paper/90 bg-shule-coffee/40 backdrop-blur-sm rounded-full px-2.5 py-1">Demo</span>
          )}
        </div>
      </Link>
      <button
        data-testid={`favorite-toggle-${product.slug}`}
        onClick={() => toggleFavorite(product.slug)}
        aria-label={fav ? "Favorilerden çıkar" : "Favorilere ekle"}
        className="absolute top-3 right-3 w-11 h-11 rounded-full bg-shule-paper/90 backdrop-blur border border-shule-line flex items-center justify-center transition-colors hover:border-shule-gold"
      >
        <Heart className={`w-4 h-4 transition-colors ${fav ? "fill-shule-gold text-shule-gold" : "text-shule-mocha"}`} />
      </button>
      <div className="pt-3 px-1 flex items-start justify-between gap-2">
        <div>
          <Link to={`/urun/${product.slug}`}>
            <h3 className="font-serif text-lg leading-tight text-shule-coffee">{product.name}</h3>
          </Link>
          <p className="text-sm mt-1">
            <span className="font-bold">{formatPrice(product.price)}</span>
            {product.compare_at && (
              <span className="ml-2 text-shule-soft line-through text-xs">{formatPrice(product.compare_at)}</span>
            )}
          </p>
        </div>
        {outOfStock ? (
          <span className="shrink-0 min-h-[44px] px-3 rounded-full border border-shule-line flex items-center text-xs font-semibold text-shule-soft">
            Stokta Yok
          </span>
        ) : hasChoices ? (
          <Link
            to={`/urun/${product.slug}`}
            data-testid={`choose-variant-${product.slug}`}
            className="shrink-0 min-h-[44px] px-3 rounded-full border border-shule-lineaccent flex items-center text-xs font-semibold hover:border-shule-gold"
            aria-label={`${product.name} seçeneklerini gör`}
          >
            Seçenekler
          </Link>
        ) : (
          <button
            data-testid={`quick-add-${product.slug}`}
            onClick={() => addToCart(product, product.variants?.[0])}
            aria-label={`${product.name} sepete ekle`}
            className="shrink-0 w-11 h-11 rounded-full border border-shule-lineaccent flex items-center justify-center transition-all duration-300 hover:bg-shule-coffee hover:text-shule-paper hover:border-shule-coffee"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
    </motion.article>
  );
}
